/* eslint-disable react/no-danger */
import { CircularProgress, Divider } from '@mui/material'
import {
	GET_FM_PARTNER_LIST_FOLLOWERS_FOLLOWERS,
	GET_FM_PARTNER_LIST_FOLLOWERS_PARTNERS,
	GET_MY_FEEDS_TABLE,
	GET_REACH_CONTENT,
	GET_TAGS_BOX,
	getFMPartnerListFollowers,
	getFMPartnerListPartners,
	getMyFeeds,
	postContentReach
} from 'api'
import { getTagsInfo } from 'api/get/reach/reach'
import clsx from 'clsx'
import { Button } from 'components/buttons'
import { Modal } from 'components/modal'
import { TagAudience } from 'components/reachModal/tagAudience'
import { useSearchDebounce } from 'hooks/useSearchDebounce'
import { Icons, Reach } from 'interfaces'
import React, { useEffect, useMemo, useState } from 'react'
import { useInfiniteQuery, useQuery } from 'react-query'
import Cookies from 'universal-cookie'
import { formatLocalTime } from 'utils'
import { ListTags } from './listTags'
import { TitleBox } from './titleBox'
import { TitleContent } from './titleContent'
import { TitleContentRecurrence } from './titleContentRecurrence'
import { ConfirmationContentProps } from './types'

const formatCategory = (category: string): string => {
	return category
		.split('_')
		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ')
}

export const ConfirmationContent: React.FC<ConfirmationContentProps> = ({
	totalData,
	documentsList,
	watchTitle,
	watchDescription,
	changeModal,
	handleSubmit,
	locationInfo,
	deliveryActive
}) => {
	const cookies = new Cookies()
	const userId = cookies.get('userId')
	const organizationId = cookies.get('organization_id')
	const hasVideo =
		totalData.basic_setup.preview?.video_link_external ||
		(totalData.basic_setup.preview?.files?.some(
			file => file.asset_type === 'external_video'
		) ??
			false)

	const { delivery_enhancements: delivery } = totalData
	const { audience, detailed_reach: detailedReach } = delivery
	const hasAudience =
		audience.tags.length > 0 ||
		audience.lists.length > 0 ||
		audience.dynamic_lists.length > 0 ||
		audience.individuals.length > 0 ||
		audience.organizations.length > 0

	const hasTags = (detailedReach?.tag.total ?? 0) > 0
	const hasStaticLists = (detailedReach?.static_list.total ?? 0) > 0
	const hasDynamicLists = (detailedReach?.dynamic_list.total ?? 0) > 0
	const hasIndividuals = (detailedReach?.individual.total ?? 0) > 0

	const dataRequestReach: Reach = {
		tags: [],
		lists: [],
		dynamic_lists: [],
		individuals: [],
		organizations: [],
		feeds: [],
		partner_lists: [],
		organization_id: organizationId
	}

	const {
		inputSearch: inputSearchTags,
		handleChangeDebounce: handleChangeDebounceTags,
		resetInput: resetInputTags
	} = useSearchDebounce('')
	const numberElement = 10
	const [isOpenTagBoxModal, setIsOpenTagBoxModal] = useState(false)
	const [page, setPage] = useState<number>(1)
	const [valueItemsTags, setValueItemsTags] = useState(numberElement)
	const [searchTags, setSearchTags] = useState('')

	const { data, isLoading } = useQuery(
		[GET_REACH_CONTENT, hasAudience],
		() => postContentReach(dataRequestReach),
		{ enabled: !hasAudience }
	)

	const reach = data?.reach.toString() ?? detailedReach?.total.toString()

	const {
		data: partnersData,
		fetchNextPage: fetchNextPagePartners,
		hasNextPage: hasNextPagePartners,
		isFetchingNextPage: isFetchingNextPagePartners
	} = useInfiniteQuery(
		[GET_FM_PARTNER_LIST_FOLLOWERS_PARTNERS, userId, organizationId, ''],
		({ pageParam = 1 }) =>
			getFMPartnerListPartners(organizationId, { page: pageParam, search: '' }),
		{
			getPreviousPageParam: firstPage =>
				firstPage?.page ? firstPage.page : undefined,
			getNextPageParam: lastPage =>
				lastPage && lastPage.page < lastPage.pages
					? lastPage.page + 1
					: undefined,
			enabled: audience.organizations.length > 0
		}
	)

	useEffect(() => {
		if (hasNextPagePartners && !isFetchingNextPagePartners)
			fetchNextPagePartners()
	}, [hasNextPagePartners, isFetchingNextPagePartners, fetchNextPagePartners])

	const partnerNames = useMemo(
		() =>
			partnersData?.pages
				.flatMap(p => p?.items ?? [])
				.filter(item =>
					audience.organizations.includes(parseInt(item.partner_id, 10))
				)
				.map(item => item.partner_name) ?? [],
		[partnersData, audience.organizations]
	)

	const {
		data: feedsData,
		fetchNextPage: fetchNextPageFeeds,
		hasNextPage: hasNextPageFeeds,
		isFetchingNextPage: isFetchingNextPageFeeds
	} = useInfiniteQuery(
		[GET_MY_FEEDS_TABLE, { userId, organizationId }, ''],
		({ pageParam = 1 }) =>
			getMyFeeds(organizationId, { page: pageParam, search: '' }),
		{
			getPreviousPageParam: firstPage =>
				firstPage?.page ? firstPage.page : undefined,
			getNextPageParam: lastPage =>
				lastPage && lastPage.page < lastPage.pages
					? lastPage.page + 1
					: undefined,
			enabled: audience.feeds.length > 0
		}
	)

	useEffect(() => {
		if (hasNextPageFeeds && !isFetchingNextPageFeeds) fetchNextPageFeeds()
	}, [hasNextPageFeeds, isFetchingNextPageFeeds, fetchNextPageFeeds])

	const feedNames = useMemo(
		() =>
			feedsData?.pages
				.flatMap(p => p?.items ?? [])
				.filter(item => audience.feeds.includes(item.id))
				.map(item => item.name) ?? [],
		[feedsData, audience.feeds]
	)

	const {
		data: partnerListsData,
		fetchNextPage: fetchNextPagePartnerLists,
		hasNextPage: hasNextPagePartnerLists,
		isFetchingNextPage: isFetchingNextPagePartnerLists
	} = useInfiniteQuery(
		[GET_FM_PARTNER_LIST_FOLLOWERS_FOLLOWERS, userId, organizationId],
		({ pageParam = 1 }) => getFMPartnerListFollowers(organizationId, pageParam),
		{
			getPreviousPageParam: firstPage =>
				firstPage?.page ? firstPage.page : undefined,
			getNextPageParam: lastPage =>
				lastPage && lastPage.page < lastPage.pages
					? lastPage.page + 1
					: undefined,
			enabled: audience.partner_lists.length > 0
		}
	)

	useEffect(() => {
		if (hasNextPagePartnerLists && !isFetchingNextPagePartnerLists)
			fetchNextPagePartnerLists()
	}, [
		hasNextPagePartnerLists,
		isFetchingNextPagePartnerLists,
		fetchNextPagePartnerLists
	])

	const partnerListNames = useMemo(
		() =>
			partnerListsData?.pages
				.flatMap(p => p?.items ?? [])
				.filter(item => audience.partner_lists.includes(item.id))
				.map(item => item.name) ?? [],
		[partnerListsData, audience.partner_lists]
	)

	const {
		data: dataTagBox,
		isLoading: isLoadingTagBox,
		hasNextPage: hasNextPageTagsReach,
		fetchNextPage: fetchNextPageTagsReach,
		isFetchingNextPage: isFetchingNextPageTagsReach
	} = useInfiniteQuery({
		queryKey: [
			GET_TAGS_BOX,
			audience.tags,
			organizationId,
			page,
			valueItemsTags,
			inputSearchTags
		],
		queryFn: ({ pageParam = 1 }) =>
			getTagsInfo(
				audience.tags,
				organizationId,
				pageParam,
				valueItemsTags,
				inputSearchTags
			),
		getNextPageParam: response => {
			const nextPage = Number(response?.page) + 1
			if (nextPage <= Number(response?.pages)) {
				return nextPage
			}
			return undefined
		},
		enabled: audience.tags.length > 0 && isOpenTagBoxModal,
		refetchOnWindowFocus: false
	})

	const contentSchedule =
		(totalData.delivery_enhancements.schedule.timing.expiration_date !== '' ||
			totalData.delivery_enhancements.schedule.custom_recurrence
				.repeat_start_date !== '') &&
		totalData.delivery_enhancements.schedule.custom_recurrence

	const eventSchedule =
		totalData.delivery_enhancements.schedule.event &&
		totalData.delivery_enhancements.schedule.event?.event_start_date !== '' &&
		totalData.delivery_enhancements.schedule.event?.event_end_date !== ''

	const recurrent =
		totalData.delivery_enhancements.schedule.timing.release_date === ''

	const listDaysRecurrence =
		totalData.delivery_enhancements.schedule.custom_recurrence
			?.repeat_every_type === 'week'
			? totalData.delivery_enhancements.schedule.custom_recurrence.repeat_on
					// eslint-disable-next-line no-useless-escape
					.replace(/[\[\]']+/g, '')
					.split(',')
			: undefined

	const eventBody = {
		event_start_date:
			totalData.delivery_enhancements.schedule.event?.event_start_date,
		event_end_date:
			totalData.delivery_enhancements.schedule.event?.event_end_date,
		event_recurrence: totalData.delivery_enhancements.schedule.event
			?.event_repeat_until
			? totalData.delivery_enhancements.schedule.event?.event_repeat_until
			: undefined
	}

	const styleRecurenceValidation =
		contentSchedule && !listDaysRecurrence && !recurrent

	return (
		<div>
			<div className="overflow-y-auto  max-h-[80vh] mt-3">
				<div className="border border-blue-primary rounded-lg p-4 my-4">
					<TitleBox
						icon={Icons.preview}
						title={
							deliveryActive.push_notification &&
							(totalData.delivery_enhancements.push_notification.is_title ||
								totalData.delivery_enhancements.push_notification.is_message)
								? 'Review Content and Push:'
								: 'Review:'
						}
					/>
					<div className="flex items-stretch gap-4">
						<div className="border border-blue-primary rounded-lg p-4 flex-grow flex flex-col h-full min-h-[197px] min-w-[200px]">
							<div className="flex-grow">
								{deliveryActive.push_notification && (
									<p className="text-[20px] text-gray-600 font-bold font-Inter mb-2">
										Content Item:
									</p>
								)}

								<p className="text-[15px] text-black-1 font-bold font-Inter">
									{watchTitle}
								</p>

								<div
									className="text-[15px] mt-3 text-black-1 font-normal font-Inter"
									dangerouslySetInnerHTML={{
										__html: watchDescription || ''
									}}
								/>
								{deliveryActive.push_notification && (
									<>
										<hr className="my-4" />
										<div className="mt-4">
											<p className="text-[20px] text-gray-600 font-bold font-Inter mb-2">
												Push Notification:
											</p>
											<p className="text-[15px] text-black-1 font-bold font-Inter">
												{totalData.delivery_enhancements.push_notification
													.is_title
													? totalData.delivery_enhancements.push_notification
															.title
													: totalData.set_up.title &&
													  totalData.set_up.title !== ''
													? `${totalData.set_up.title} (from content)`
													: `${watchTitle} (from content)`}
											</p>
											<div className="text-[15px] text-black-1 font-normal font-Inter mt-2">
												{totalData.delivery_enhancements.push_notification
													.is_message
													? totalData.delivery_enhancements.push_notification
															.message
													: totalData.set_up.message &&
													  totalData.set_up.message !== ''
													? `${
															totalData.set_up.description?.replace(
																/<[^>]*>/g,
																''
															) || ''
													  } (from content)`
													: `${
															watchDescription.replace(/<[^>]*>/g, '') || ''
													  } (from content)`}
											</div>
										</div>
									</>
								)}
							</div>
						</div>
					</div>
				</div>
				<div className="border border-blue-primary rounded-lg p-4 my-4">
					<TitleBox icon={Icons.displaySettings} title="App Content Setup:" />
					<div className="flex items-center">
						<TitleContent
							title="Type:"
							content={formatCategory(totalData.basic_setup.category)}
						/>
						<TitleContent
							title="Sharing:"
							content={totalData.basic_setup.sharing ? 'On' : 'Off'}
						/>
						<TitleContent
							title="Saving:"
							content={totalData.basic_setup.saving ? 'On' : 'Off'}
						/>
						<TitleContent
							title="Push:"
							content={deliveryActive.push_notification ? 'On' : 'Off'}
						/>
					</div>

					<div
						className={`flex ${
							listDaysRecurrence || recurrent ? 'flex-col' : 'flex-row'
						} gap-2 pt-10`}
					>
						{contentSchedule && (
							<TitleContentRecurrence
								type="content"
								endTitle={
									totalData.delivery_enhancements.schedule.custom_recurrence
										.ends.repeat_end_type === 'after_ocurrence'
										? 'after'
										: 'on'
								}
								publish={formatLocalTime(
									totalData.delivery_enhancements.schedule.timing
										.release_date as string,
									'MM/DD/YYYY h:mm A'
								)}
								expire={formatLocalTime(
									totalData.delivery_enhancements.schedule.timing
										.expiration_date as string,
									'MM/DD/YYYY h:mm A'
								)}
								recurrent={recurrent}
								endsRecurrent={
									totalData.delivery_enhancements.schedule.custom_recurrence
										.ends.repeat_end_type === 'never'
										? 'Never'
										: totalData.delivery_enhancements.schedule.custom_recurrence
												.ends.repeat_end_type === 'after_ocurrence'
										? `${totalData.delivery_enhancements.schedule.custom_recurrence.ends.repeat_end_value} Occurrences`
										: formatLocalTime(
												totalData.delivery_enhancements.schedule
													.custom_recurrence.ends.repeat_end_date as string,
												'MM/DD/YYYY h:mm A'
										  )
								}
								publishRecurrent={formatLocalTime(
									totalData.delivery_enhancements.schedule.custom_recurrence
										.repeat_start_date as string,
									'MM/DD/YYYY h:mm A'
								)}
								// eslint-disable-next-line max-len
								repeatEveryRecurrent={`${totalData.delivery_enhancements.schedule.custom_recurrence.repeat_every_value} ${totalData.delivery_enhancements.schedule.custom_recurrence?.repeat_every_type}`}
								repeatOnRecurrent={formatLocalTime(
									totalData.delivery_enhancements.schedule.custom_recurrence
										.repeat_on as string,
									'MM/DD/YYYY h:mm A'
								)}
								listDaysRecurrence={listDaysRecurrence}
								event={eventBody}
								showGrayLine={styleRecurenceValidation}
							/>
						)}
						{eventSchedule && (
							<div className={styleRecurenceValidation ? 'pl-7' : ''}>
								<TitleContentRecurrence type="event" event={eventBody} />
							</div>
						)}
					</div>

					<div>
						{totalData.delivery_enhancements.push_notification.timing
							.release_date && (
							<div className="flex items-center gap-2">
								<TitleContent
									title="Push Schedule:"
									content={formatLocalTime(
										totalData.delivery_enhancements.push_notification.timing
											.release_date as string,
										'MM/DD/YYYY hh:mm A'
									)}
								/>
							</div>
						)}
					</div>

					{totalData.delivery_enhancements.push_notification.custom_recurrence
						.repeating_schedule && (
						<>
							<br />
							<TitleContentRecurrence
								type="push"
								endTitle={
									totalData.delivery_enhancements.push_notification
										.custom_recurrence.ends.repeat_end_type ===
									'after_ocurrence'
										? 'after'
										: 'on'
								}
								publish={formatLocalTime(
									totalData.delivery_enhancements.push_notification.timing
										.release_date as string,
									'MM/DD/YYYY '
								)}
								recurrent={
									totalData.delivery_enhancements.push_notification.timing
										.release_date === null ||
									totalData.delivery_enhancements.push_notification.timing
										.release_date === ''
								}
								endsRecurrent={
									totalData.delivery_enhancements.push_notification
										.custom_recurrence.ends.repeat_end_type === 'never'
										? 'Never'
										: totalData.delivery_enhancements.push_notification
												.custom_recurrence.ends.repeat_end_type ===
										  'after_ocurrence'
										? `${totalData.delivery_enhancements.push_notification.custom_recurrence.ends.after_ocurrences} Occurrences`
										: formatLocalTime(
												totalData.delivery_enhancements.push_notification
													.custom_recurrence.ends.repeat_end_date as string,
												'MM/DD/YYYY h:mm A'
										  )
								}
								publishRecurrent={formatLocalTime(
									totalData.delivery_enhancements.push_notification
										.custom_recurrence.repeat_start_date as string,
									'MM/DD/YYYY h:mm A'
								)}
								// eslint-disable-next-line max-len
								repeatEveryRecurrent={`${totalData.delivery_enhancements.push_notification.custom_recurrence.repeat_every_value} ${totalData.delivery_enhancements.push_notification.custom_recurrence?.repeat_every_type}`}
								repeatOnRecurrent={formatLocalTime(
									totalData.delivery_enhancements.push_notification
										.custom_recurrence.repeat_on as string,
									'MM/DD/YYYY'
								)}
								listDaysRecurrence={
									totalData.delivery_enhancements.push_notification
										.custom_recurrence?.repeat_every_type === 'week'
										? totalData.delivery_enhancements.push_notification.custom_recurrence.repeat_on
												// eslint-disable-next-line no-useless-escape
												.replace(/[\[\]']+/g, '')
												.split(',')
										: undefined
								}
							/>
						</>
					)}

					<div>
						{totalData.delivery_enhancements.good_link &&
							totalData.delivery_enhancements.good_link.link_call_action !==
								'' && (
								<div className="flex items-center gap-2">
									<TitleContent
										title="Goodlink:"
										content={
											totalData.delivery_enhancements.good_link.link_call_action
										}
									/>
									{totalData.delivery_enhancements.good_link
										.sublink_call_action !== '' && (
										<TitleContent
											title="Sublink:"
											content={
												totalData.delivery_enhancements.good_link
													.sublink_call_action
											}
										/>
									)}
								</div>
							)}
					</div>
					{totalData.delivery_enhancements.location &&
						totalData.delivery_enhancements.location[0]?.address && (
							<TitleContent
								title="Location:"
								content={
									totalData.delivery_enhancements.location[0]?.address as string
								}
							/>
						)}
					{totalData.delivery_enhancements.location &&
						totalData.delivery_enhancements.location[0]?.location_id &&
						locationInfo && (
							<TitleContent title="Location:" content={locationInfo.label} />
						)}
				</div>
				<div className="border border-blue-primary rounded-lg p-4 my-4">
					<TitleBox icon={Icons.assets} title="Assets:" />
					<div className="flex items-center">
						<TitleContent
							title="Documents:"
							content={documentsList.length.toString() || '0'}
						/>
						<TitleContent
							title="Images:"
							content={
								totalData.basic_setup.preview?.files?.length.toString() || '0'
							}
						/>
						<TitleContent
							title="Videos:"
							content={hasVideo ? '1' : '0'}
							noBorder
						/>
					</div>
				</div>
				{audience.organizations.length > 0 && (
					<div className="border border-blue-primary rounded-lg p-4 my-4">
						<TitleBox icon={Icons.share} title="Share with a Partner:" />
						<div className="flex items-center">
							<ListTags listTags={partnerNames} />
						</div>
					</div>
				)}

				{audience.feeds.length > 0 && (
					<div className="border border-blue-primary rounded-lg p-4 my-4">
						<TitleBox icon={Icons.share} title="Share with a Partner Feed:" />
						<div className="flex items-center">
							<ListTags listTags={feedNames} />
						</div>
					</div>
				)}

				{audience.partner_lists.length > 0 && (
					<div className="border border-blue-primary rounded-lg p-4 my-4">
						<TitleBox
							icon={Icons.share}
							title="Share with a FM Partner List:"
						/>
						<div className="flex items-center">
							<ListTags listTags={partnerListNames} />
						</div>
					</div>
				)}

				<div className="border border-blue-primary rounded-lg p-4 my-4">
					<TitleBox icon={Icons.audience} title="Audience:" />
					{isLoading ? (
						<div className="flex h-full w-full justify-center items-center">
							<CircularProgress disableShrink />
						</div>
					) : (
						<>
							<div className="overflow-y-auto max-h-[300px]">
								{hasAudience && (
									<>
										{hasTags && (
											<>
												<TitleContent
													title="Tags:"
													content={detailedReach?.tag.total.toString() || '0'}
													noBorder
													isFull
													isBig
													onClickContent={() => {
														setIsOpenTagBoxModal(true)
													}}
												/>
												<ListTags listTags={detailedReach?.tag.list || []} />
												<Divider />
											</>
										)}
										{hasStaticLists && (
											<>
												<TitleContent
													title="Static Lists:"
													content={
														detailedReach?.static_list.total.toString() || '0'
													}
													noBorder
													isFull
													isBig
												/>
												<ListTags
													listTags={detailedReach?.static_list.list || []}
												/>
												<Divider />
											</>
										)}
										{hasDynamicLists && (
											<>
												<TitleContent
													title="Dynamic Lists:"
													content={
														detailedReach?.dynamic_list.total.toString() || '0'
													}
													noBorder
													isFull
													isBig
												/>
												<ListTags
													listTags={detailedReach?.dynamic_list.list || []}
												/>
												<Divider />
											</>
										)}
										{hasIndividuals && (
											<>
												<TitleContent
													title="Individuals:"
													content={
														detailedReach?.individual.total.toString() || '0'
													}
													noBorder
													isFull
													isBig
												/>
												<ListTags
													listTags={detailedReach?.individual.list || []}
												/>
											</>
										)}
									</>
								)}

								{!hasAudience && !isLoading && (
									<TitleContent
										title=" All of My People:"
										content={reach ?? '0'}
										noBorder
										isFull
										isBig
									/>
								)}
							</div>
							<div className="mt-4 pt-2 border-t-2 border-blue-primary flex items-center gap-2 justify-between">
								<TitleBox title="Total:" />
								<p
									className={clsx(
										`text-[22px] text-white font-normal font-Inter border border-blue-primary bg-blue-primary rounded-full px-2 ${
											reach && reach.toString().length === 1
												? 'w-[44px] h-[44px] flex items-center justify-center'
												: 'max-w-max'
										} capitalize mb-4`
									)}
								>
									{reach || 0}
								</p>
							</div>
						</>
					)}
				</div>
				{isOpenTagBoxModal && (
					<Modal
						setOpen={setIsOpenTagBoxModal}
						open={isOpenTagBoxModal}
						type="normal"
						sizeModal="h-[418px] p-2"
						maxWidth="max-w-[302px]"
						marginL
					>
						<TagAudience
							resetInput={resetInputTags}
							handleChangeDebounce={handleChangeDebounceTags}
							setSearchTags={setSearchTags}
							searchTags={searchTags}
							dataTagsAudience={dataTagBox}
							isLoadingTagsAudience={isLoadingTagBox}
							page={page}
							setPage={setPage}
							valueItems={valueItemsTags}
							setValueItems={setValueItemsTags}
							fetchNextPageTagsReach={fetchNextPageTagsReach}
							hasNextPageTagsReach={hasNextPageTagsReach}
							isFetchingNextPageTagsReach={isFetchingNextPageTagsReach}
						/>
					</Modal>
				)}
			</div>
			<div className="flex items-center justify-end mt-4 gap-6">
				<Button
					type="button"
					label="Back to editing"
					color="Gray3"
					className="!min-w-[130px]"
					iconRight={Icons.edit}
					onClick={() => changeModal(false)}
				/>
				<Button
					type="button"
					label="Publish now"
					color="Primary"
					iconRight={Icons.playArrow}
					className="!min-w-[130px]"
					onClick={() => handleSubmit('published')}
				/>
			</div>
		</div>
	)
}
